import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import moment from 'moment';
import api from '../services/api';
import { FaChevronUp, FaChevronDown, FaSpinner, FaExclamationTriangle, FaWalking, FaBicycle, FaCar } from 'react-icons/fa';
import { FiClock, FiPauseCircle, FiPlayCircle, FiBriefcase, FiMapPin, FiFlag, FiNavigation, FiTruck, FiUser, FiCalendar } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import TripDetails from '../components/TripDetails';
import './LocationPage.css';
import '../styles/pages/Dashboard.css';

// --- Helper Functions & Initial Setup ---

// Decode Google Polyline
const decodeGooglePolyline = (encoded) => {
    const points = [];
    let index = 0, lat = 0, lng = 0;
    while (index < encoded.length) {
        let b, shift = 0, result = 0;
        do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
        const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1)); lat += dlat;
        shift = 0; result = 0;
        do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
        const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1)); lng += dlng;
        points.push([lat * 1e-5, lng * 1e-5]);
    }
    return points;
};

const normalizeTrackPoints = (trackPoints) => {
    if (!Array.isArray(trackPoints) || trackPoints.length === 0) return [];
    return trackPoints
        .map((point) => {
            const lat = Number(point?.lat);
            const lng = Number(point?.lng);
            const timestamp = new Date(point?.timestamp).getTime();
            if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(timestamp)) {
                return null;
            }
            return {
                lat,
                lng,
                timestamp,
                speed: Number.isFinite(Number(point?.speed)) ? Number(point.speed) : null,
                accuracy: Number.isFinite(Number(point?.accuracy)) ? Number(point.accuracy) : null
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.timestamp - b.timestamp);
};

const getTrackPointsForTrip = (points, trip) => {
    if (!trip?.startTime || !trip?.endTime || !Array.isArray(points)) return [];
    const start = new Date(trip.startTime).getTime();
    const end = new Date(trip.endTime).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return [];
    return points.filter((point) => point.timestamp >= start && point.timestamp <= end);
};

// Function to get place name from coordinates using Google Geocoding API
const MAP_KEY = 'AIzaSyDzeqjM4WeUbZAv3p5GivDFt3WXJSFDaQw';

const getPlaceName = async (lat, lng, setPlaceNames) => {
    const key = `${lat},${lng}`;
    
    try {
        const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${MAP_KEY}`
        );
        const data = await response.json();
        
        if (data.results && data.results.length > 0) {
            const placeName = data.results[0].formatted_address;
            setPlaceNames(prev => ({ ...prev, [key]: placeName }));
            return placeName;
        }
        return null;
    } catch (error) {
        console.error('Error fetching place name:', error);
        return null;
    }
};

// Function to get battery level color
const getBatteryColor = (level) => {
    if (!level) return '#6c757d';
    if (level > 0.5) return '#28a745';
    if (level > 0.2) return '#ff9800';
    return '#dc3545';
};

// Function to get accuracy level color
const getAccuracyColor = (accuracy) => {
    if (!accuracy) return '#6c757d';
    if (accuracy < 10) return '#28a745';
    if (accuracy < 50) return '#ff9800';
    return '#dc3545';
};

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom Marker Icons - Circular design (color + compact icon)
const createCircularIcon = (
    color,
    markerId = '',
    { iconClass = 'fas fa-circle', type = '', title = '' } = {}
) => {
    const safeMarkerId = markerId != null ? String(markerId) : '';
    const safeType = type != null ? String(type) : '';
    const safeIconClass = iconClass != null ? String(iconClass) : 'fas fa-circle';
    const safeTitle = title != null ? String(title).replace(/"/g, '&quot;') : '';

    return L.divIcon({
        className: `custom-marker`,
        html: `
            <div
                class="marker-dot"
                data-marker-id="${safeMarkerId}"
                data-marker-type="${safeType}"
                title="${safeTitle}"
                style="--marker-color: ${color};"
            >
                <span class="marker-icon"><i class="${safeIconClass}"></i></span>
            </div>
        `,
        iconSize: [26, 26],
        iconAnchor: [13, 13]
    });
};

// Custom avatar icon for latest/team locations (supports stale + selected ring)
const createAvatarIcon = (avatarUrl, initials, isStale = false, isSelected = false) => {
    const staleClass = isStale ? 'stale' : '';
    const selectedClass = isSelected ? 'selected' : '';
    return L.divIcon({
        className: `avatar-marker ${staleClass} ${selectedClass}`.trim(),
        html: avatarUrl
            ? `<img src="${avatarUrl}" alt="avatar" class="marker-avatar ${staleClass}" />`
            : `<div class="marker-initials ${staleClass}">${initials}</div>`,
        iconSize: [40, 40],
        iconAnchor: [20, 20],
    });
};

const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const d = moment.duration(seconds, 'seconds');
    if (d.asDays() >= 1) {
        const days = Math.floor(d.asDays());
        const hours = d.hours();
        return `${days}d ${hours}h`;
    }
    if (d.asHours() >= 1) return `${Math.floor(d.asHours())}h ${d.minutes()}m`;
    return `${d.minutes()}m`;
};

const getTripTypeIcon = (tripType) => {
    if (tripType === 'walking') return FaWalking;
    if (tripType === 'cycling') return FaBicycle;
    return FaCar;
};

// --- React Components ---

const MapUpdater = ({ center, zoom, bounds }) => {
    const map = useMap();
    useEffect(() => {
        if (bounds && bounds.isValid && bounds.isValid()) {
            // Only use bounds if they're valid and no manual centering is happening
            map.fitBounds(bounds, { padding: [50, 50] });
        } else if (center && Array.isArray(center) && center.length === 2) {
            // Manual centering takes priority - use flyTo for smoother animation
            const targetZoom = zoom || map.getZoom();
            map.flyTo(center, targetZoom, {
                duration: 0.8, // Smooth animation duration
                easeLinearity: 0.25
            });
        }
    }, [center, zoom, bounds, map]);
    return null;
};

// Component to handle popup opening/closing
const PopupController = ({ openPopupId }) => {
    const map = useMap();
    
    useEffect(() => {
        if (!openPopupId || !map) return;
        
        // Close all popups first
        map.closePopup();
        
        // Small delay to ensure markers are rendered, then find and open the target popup
        const timer = setTimeout(() => {
            let targetMarker = null;
            
            map.eachLayer((layer) => {
                if (layer instanceof L.Marker && layer.getPopup()) {
                    const popup = layer.getPopup();
                    if (popup && popup.options && popup.options.markerId === openPopupId) {
                        targetMarker = layer;
                    }
                }
            });
            
            if (targetMarker) {
                targetMarker.openPopup();
            }
        }, 150);
        
        return () => clearTimeout(timer);
    }, [openPopupId, map]);
    
    return null;
};

const LocationPage = () => {
    // --- State Management ---
    const { company } = useAuth();
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [locationViewMode, setLocationViewMode] = useState('employees');
    const [employeeLocationSource, setEmployeeLocationSource] = useState('phone');
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [vehicleLocations, setVehicleLocations] = useState([]);
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
    const [isLoading, setIsLoading] = useState(false);
    
    // Data states
    const [trips, setTrips] = useState([]);
    const [selectedTripId, setSelectedTripId] = useState(null);
    const [polylines, setPolylines] = useState([]);
    const [visitedPlaces, setVisitedPlaces] = useState([]);
    const [trackPoints, setTrackPoints] = useState([]);
    const [workLogs, setWorkLogs] = useState([]);
    const [filteredWorkLogs, setFilteredWorkLogs] = useState([]);
    const [attendanceData, setAttendanceData] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [timelineData, setTimelineData] = useState([]);
    
    // Latest location states
    const [latestLocation, setLatestLocation] = useState(null);
    const [placeNames, setPlaceNames] = useState({});
    const [employeeLocations, setEmployeeLocations] = useState([]);
    const [selectedTeamLocation, setSelectedTeamLocation] = useState(null);
    const teamMarkersRef = useRef({});
    const teamMapRef = useRef(null);
    
    // Map states
    const [mapCenter, setMapCenter] = useState([15.4989, 73.8278]); // Goa
    const [mapZoom, setMapZoom] = useState(11);
    const [mapBounds, setMapBounds] = useState(null);
    const [isTripDetailsCollapsed, setIsTripDetailsCollapsed] = useState(false);
    const [selectedMarkerId, setSelectedMarkerId] = useState(null);
    const [openPopupId, setOpenPopupId] = useState(null);
    const [expandedTimelineItem, setExpandedTimelineItem] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    const renderLocationMarkerCard = ({ employee, coords, placeName, batteryLevel, accuracy, lastUpdated, isStale, title }) => {
        if (!employee || !coords) return null;
        const initials = `${employee.firstName?.charAt(0) || ''}${employee.lastName?.charAt(0) || ''}`.toUpperCase();
        return (
            <div className={`marker-popup marker-popup--location-card ${isStale ? 'is-stale' : ''}`}>
                <div className="popup-header">
                    {employee.profilePicture ? (
                        <img src={employee.profilePicture} alt={`${employee.firstName} ${employee.lastName}`} className="popup-avatar" />
                    ) : (
                        <div className="popup-initials">{initials}</div>
                    )}
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <h4 style={{ margin: 0, lineHeight: 1.15 }}>{employee.firstName} {employee.lastName}</h4>
                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span className="location-pill location-pill-neutral">{title}</span>
                            {isStale && <span className="location-pill location-pill-warning">Stale</span>}
                        </div>
                    </div>
                </div>
                <div className="popup-details">
                    <p className="popup-address">{placeName || 'Loading address...'}</p>
                    <div className="popup-metrics">
                        <div className="popup-metric battery" style={{ '--battery-color': getBatteryColor(batteryLevel) }}>
                            <i className="popup-metric-icon fas fa-battery-half" aria-hidden="true"></i>
                            <span className="metric-value">{batteryLevel ? `${batteryLevel}%` : 'N/A'}</span>
                        </div>
                        <div className="popup-metric accuracy" style={{ '--accuracy-color': getAccuracyColor(accuracy) }}>
                            <i className="popup-metric-icon fas fa-crosshairs" aria-hidden="true"></i>
                            <span className="metric-value">{accuracy ? `${Math.round(accuracy)}m` : 'N/A'}</span>
                        </div>
                    </div>
                    {lastUpdated && (
                        <p className="popup-time">Updated: {new Date(lastUpdated).toLocaleString()}</p>
                    )}
                </div>
            </div>
        );
    };

    // --- Data Fetching & Processing ---

    // Fetch employees
    useEffect(() => {
        const fetchEmployees = async () => {
            if (!company || !company.id) return;
            try {
                const response = await api.get(`/api/companies/${company.id}/employees`);
                if (response.data?.is_success) {
                    setEmployees(response.data.employees || []);
                }
            } catch (error) {
                console.error("Error fetching employees:", error);
            }
        };
        fetchEmployees();
    }, [company]);

    useEffect(() => {
        const fetchVehicles = async () => {
            if (!company?.id) return;
            try {
                const response = await api.get(`/api/companies/${company.id}/vehicles`);
                if (response.data?.is_success) {
                    setVehicles(response.data.vehicles || []);
                }
            } catch (error) {
                console.error('Error fetching vehicles:', error);
            }
        };
        fetchVehicles();
    }, [company]);

    // Fetch latest location for selected employee
    useEffect(() => {
        if (locationViewMode !== 'employees' || !selectedEmployee || !company?.id) {
            setLatestLocation(null);
            return;
        }

        const fetchLatestLocation = async () => {
            try {
                const response = await api.get(`/api/companies/${company.id}/latest-locations?excludeNoLocation=true`);
                if (response.data?.data?.locations) {
                    const locations = response.data.data.locations;
                    const employeeLocation = locations.find(loc => 
                        loc.employee && loc.employee.id === parseInt(selectedEmployee)
                    );
                    
                    if (employeeLocation && employeeLocation.location?.coords) {
                        setLatestLocation(employeeLocation);
                        
                        // Fetch place name for the latest location
                        getPlaceName(
                            employeeLocation.location.coords.latitude,
                            employeeLocation.location.coords.longitude,
                            setPlaceNames
                        );
                    } else {
                        setLatestLocation(null);
                    }
                }
            } catch (error) {
                console.error("Error fetching latest location:", error);
                setLatestLocation(null);
            }
        };
        
        fetchLatestLocation();
    }, [selectedEmployee, company?.id, locationViewMode]);

    // Fetch team latest locations when no employee selected
    useEffect(() => {
        const fetchTeamLocations = async () => {
            if (!company?.id) return;
            if (locationViewMode === 'employees' && selectedEmployee) return;
            if (locationViewMode === 'vehicles' && selectedVehicle) return;
            try {
                if (locationViewMode === 'vehicles') {
                    const locationsRes = await api.get(`/api/companies/${company.id}/latest-vehicle-locations?excludeNoLocation=true`);
                    if (locationsRes.data?.data?.locations) {
                        setVehicleLocations(locationsRes.data.data.locations);
                    }
                    return;
                }

                const locationsRes = await api.get(`/api/companies/${company.id}/latest-locations?excludeNoLocation=true`);
                if (locationsRes.data?.data?.locations) {
                    const locations = locationsRes.data.data.locations;
                    setEmployeeLocations(locations);
                    locations.forEach(location => {
                        if (location.location?.coords) {
                            getPlaceName(
                                location.location.coords.latitude,
                                location.location.coords.longitude,
                                setPlaceNames
                            );
                        }
                    });
                } else {
                    setEmployeeLocations([]);
                }
            } catch (err) {
                console.error('Error fetching team locations:', err);
                setEmployeeLocations([]);
            }
        };
        fetchTeamLocations();
    }, [company?.id, selectedEmployee, selectedVehicle, locationViewMode]);

    // Default-select a team member on load (most recently updated valid location)
    useEffect(() => {
        if (selectedEmployee) return; // only for team view
        if (!employeeLocations || employeeLocations.length === 0) return;
        if (selectedTeamLocation) return;

        const valid = employeeLocations
            .filter(l => l?.employee?.id && l?.location?.coords && l?.lastUpdated)
            .sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));

        const defaultItem = valid[0];
        if (!defaultItem) return;

        setSelectedTeamLocation(defaultItem);

        // Center once markers are mounted (card is fixed overlay; popup remains optional)
        setTimeout(() => {
            const empId = defaultItem.employee.id;
            const marker = teamMarkersRef.current?.[empId];
            const coords = defaultItem.location.coords;
            if (teamMapRef.current && marker && coords) {
                teamMapRef.current.setView([coords.latitude, coords.longitude], 16);
            }
        }, 250);
    }, [selectedEmployee, employeeLocations, selectedTeamLocation]);

    // Center map on latest location if no historical data
    useEffect(() => {
        if (latestLocation && latestLocation.location?.coords && 
            timelineData.length === 0 && trips.length === 0) {
            setMapCenter([latestLocation.location.coords.latitude, latestLocation.location.coords.longitude]);
            setMapZoom(15);
        }
    }, [latestLocation, timelineData.length, trips.length]);

    // Do not auto-open/auto-select the latest-location card; show it on marker click.

    // Fetch all timeline data
    useEffect(() => {
        const activeId = locationViewMode === 'vehicles' ? selectedVehicle : selectedEmployee;
        if (!activeId || !selectedDate) {
            setTrips([]); setPolylines([]); setVisitedPlaces([]); setWorkLogs([]); setAttendanceData(null); setTasks([]);
            setTrackPoints([]);
            setSelectedMarkerId(null);
            setOpenPopupId(null);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            setMapBounds(null);
            setPolylines({});
            setTrips([]);
            setSelectedTripId(null);
            setSelectedMarkerId(null);
            setOpenPopupId(null);
            const formattedDate = moment(selectedDate).format('YYYY-MM-DD');
            
            try {
                if (locationViewMode === 'vehicles') {
                    const locResponse = await api.get(`/api/vehicles/${selectedVehicle}/location-tracking?date=${formattedDate}&simplify=0.00003&snap=true&includeStats=true&includePlaces=true&timeGapThreshold=10&stopThreshold=5`);
                    if (locResponse.data?.is_success && locResponse.data.data) processLocationData(locResponse.data.data);
                    else { setPolylines([]); setVisitedPlaces([]); setTrips([]); setTrackPoints([]); }
                    setWorkLogs([]);
                    setAttendanceData(null);
                    setTasks([]);
                } else {
                    if (employeeLocationSource === 'tracker') {
                        const locResponse = await api.get(`/api/employees/${selectedEmployee}/tracker-location-tracking?date=${formattedDate}&simplify=0.00003&snap=false&includeStats=true&includePlaces=true&includeTrackPoints=false&timeGapThreshold=10&stopThreshold=5`);
                        if (locResponse.data?.is_success && locResponse.data.data) processLocationData(locResponse.data.data);
                        else { setPolylines([]); setVisitedPlaces([]); setTrips([]); setTrackPoints([]); }
                        setWorkLogs([]);
                        setAttendanceData(null);
                        setTasks([]);
                    } else {
                        const [locResponse, wlResponse, attResponse, tasksResponse] = await Promise.all([
                            api.get(`/api/location/tracking?userId=${selectedEmployee}&date=${formattedDate}&simplify=0.00003&snap=true&includeStats=true&includePlaces=true&timeGapThreshold=10&stopThreshold=5`),
                            api.get(`/api/companies/${selectedEmployee}/worklogs`),
                            api.get(`/api/companies/attendance/${selectedEmployee}/${formattedDate}`),
                            api.get(`/api/companies/${company?.id}/employees/${selectedEmployee}/task-timeline?date=${formattedDate}`)
                        ]);

                        if (locResponse.data?.is_success && locResponse.data.data) processLocationData(locResponse.data.data);
                        else { setPolylines([]); setVisitedPlaces([]); setTrips([]); setTrackPoints([]); }
                        if (wlResponse.data?.is_success) setWorkLogs(wlResponse.data.workLogs || []);
                        if (attResponse.data?.is_success && attResponse.data.attendanceRecords?.length > 0) setAttendanceData(attResponse.data.attendanceRecords[0]);
                        else setAttendanceData(null);
                        if (tasksResponse?.data?.is_success) setTasks(tasksResponse.data.tasks || []);
                        else setTasks([]);
                    }
                }

            } catch (error) {
                console.error("Error fetching data:", error);
                setPolylines([]); setVisitedPlaces([]); setTrips([]); setTrackPoints([]); setWorkLogs([]); setAttendanceData(null); setTasks([]);
            }
            setIsLoading(false);
        };
        fetchData();
    }, [selectedEmployee, selectedVehicle, selectedDate, locationViewMode, employeeLocationSource]);

    // Filter worklogs
    useEffect(() => {
        setFilteredWorkLogs(workLogs.filter(log => moment(log.workDate).isSame(selectedDate, 'day')));
    }, [workLogs, selectedDate]);

    // Process location data (day payload has trips/places only - no full-day trackPoints)
    const processLocationData = (locationData) => {
        const allPolylines = {};
        const places = [];
        const processedTrips = [];

        locationData.trips?.forEach((trip, index) => {
            if (trip.stats?.distance > 0) {
                const tripId = `trip-${index}`;
                // Prefer backend mode (walking / cycling / driving); fallback for older archives
                const mode = trip.stats?.transportationMode;
                let tripType = mode === 'walking' || mode === 'cycling' || mode === 'driving'
                    ? mode
                    : 'driving';
                if (!mode) {
                    const { averageSpeed } = trip.stats;
                    if (averageSpeed < 8) tripType = 'walking';
                    else if (averageSpeed < 15) tripType = 'cycling';
                }

                const tripWithId = { id: tripId, ...trip, tripType };
                processedTrips.push(tripWithId);

                const polyline = trip.snappedPolyline || trip.encodedPolyline;
                if (polyline) allPolylines[tripId] = decodeGooglePolyline(polyline);
            }
        });
        
        locationData.placesVisited?.forEach((place, index) => places.push({ id: `place-${index}`, ...place }));
        
        setTrips(processedTrips);
        setPolylines(allPolylines);
        setVisitedPlaces(places);
        setTrackPoints([]);

        if (processedTrips.length > 0) {
            const firstTrip = processedTrips[0];
            handleTripSelect(firstTrip.id, allPolylines[firstTrip.id]);
        } else if (places.length > 0) {
            setMapBounds(null);
            setMapCenter([places[0].lat, places[0].lng]);
            setMapZoom(15);
        }
    };

    // Generate unified timeline
    useEffect(() => {
        const items = [];
        if (attendanceData?.checkInTime) items.push({ id: 'att-in', type: 'attendance', subType: 'check-in', timestamp: new Date(attendanceData.checkInTime), data: attendanceData });
        if (attendanceData?.checkOutTime) items.push({ id: 'att-out', type: 'attendance', subType: 'check-out', timestamp: new Date(attendanceData.checkOutTime), data: attendanceData });
        
        // Add break start and end events
        if (attendanceData?.breaks) {
            attendanceData.breaks.forEach((breakRecord) => {
                if (breakRecord.startTime) {
                    items.push({ 
                        id: `break-start-${breakRecord.id}`, 
                        type: 'break', 
                        subType: 'start', 
                        timestamp: new Date(breakRecord.startTime), 
                        data: breakRecord 
                    });
                }
                if (breakRecord.endTime) {
                    items.push({ 
                        id: `break-end-${breakRecord.id}`, 
                        type: 'break', 
                        subType: 'end', 
                        timestamp: new Date(breakRecord.endTime), 
                        data: breakRecord 
                    });
                }
            });
        }
        
        filteredWorkLogs.forEach((log) => items.push({ id: `wl-${log.id}`, type: 'worklog', timestamp: new Date(log.workDate), data: log }));
        visitedPlaces.forEach((place) => { if (place.arriveTime) items.push({ id: `vp-${place.id}`, type: 'place', timestamp: new Date(place.arriveTime), data: place }); });
        
        // Add trip start and end events
        trips.forEach((trip) => {
            if (trip.startTime) {
                items.push({ 
                    id: `trip-start-${trip.id}`, 
                    type: 'trip', 
                    subType: 'start', 
                    timestamp: new Date(trip.startTime), 
                    data: trip 
                });
            }
            if (trip.endTime) {
                items.push({ 
                    id: `trip-end-${trip.id}`, 
                    type: 'trip', 
                    subType: 'end', 
                    timestamp: new Date(trip.endTime), 
                    data: trip 
                });
            }
        });

        // Add task started/resolved events for selected day
        if (tasks && tasks.length > 0 && selectedDate) {
            const dayStart = moment(selectedDate).startOf('day');
            const dayEnd = moment(selectedDate).endOf('day');
            tasks.forEach((task) => {
                if (task?.startedAt && moment(task.startedAt).isBetween(dayStart, dayEnd, null, '[]')) {
                    items.push({
                        id: `task-start-${task.id}`,
                        type: 'task',
                        subType: 'started',
                        timestamp: new Date(task.startedAt),
                        data: task
                    });
                }
                if (task?.resolvedAt && moment(task.resolvedAt).isBetween(dayStart, dayEnd, null, '[]')) {
                    items.push({
                        id: `task-resolved-${task.id}`,
                        type: 'task',
                        subType: 'resolved',
                        timestamp: new Date(task.resolvedAt),
                        data: task
                    });
                }
            });
        }
        
        setTimelineData(items.sort((a, b) => a.timestamp - b.timestamp));
    }, [attendanceData, filteredWorkLogs, visitedPlaces, trips, tasks, selectedDate]);

    // Handle marker selection styling dynamically
    useEffect(() => {
        // Remove selection styling from all markers
        const allMarkerDots = document.querySelectorAll('.marker-dot');
        allMarkerDots.forEach(dot => {
            const marker = dot.closest('.custom-marker');
            if (marker) {
                marker.classList.remove('marker-selected');
            }
        });

        // Apply selection styling to the selected marker
        if (selectedMarkerId) {
            const selectedMarkerDot = document.querySelector(`[data-marker-id="${selectedMarkerId}"]`);
            if (selectedMarkerDot) {
                const marker = selectedMarkerDot.closest('.custom-marker');
                if (marker) {
                    marker.classList.add('marker-selected');
                }
            }
        }
    }, [selectedMarkerId]);

    // --- Event Handlers ---
    const handleTripSelect = (tripId, polylineOverride = null) => {
        setSelectedTripId(tripId);
        setSelectedMarkerId(null);
        setOpenPopupId(null);
        setExpandedTimelineItem(null); // Clear timeline expansion when selecting trips
        const tripLine = polylineOverride || polylines[tripId];
        if (tripLine && tripLine.length > 0) {
            setMapBounds(L.latLngBounds(tripLine));
        }
    };
    
    // Helper function to determine if a timeline item is selected
    const isTimelineItemSelected = (item) => {
        if (!selectedMarkerId) return false;
        
        switch (item.type) {
            case 'place':
                return selectedMarkerId === `place-${item.data.id}`;
            case 'worklog':
                return selectedMarkerId === `worklog-${item.data.id}`;
            case 'attendance':
                return selectedMarkerId === `attendance-${item.subType}`;
            case 'break':
                return selectedMarkerId === `break-${item.subType}-${item.data.id}`;
            case 'trip':
                return selectedMarkerId === `trip-${item.subType}-${item.data.id}`;
            case 'task':
                return selectedMarkerId === `task-${item.data.id}`;
            default:
                return false;
        }
    };
    
    // Handle deselection when clicking elsewhere
    const handleDeselect = () => {
        setSelectedMarkerId(null);
        setOpenPopupId(null);
        setExpandedTimelineItem(null);
    };
    
    const handleTimelineItemClick = (item) => {
        // Helper function to center map on coordinates
        const centerMapOnLocation = (lat, lng, zoomLevel = 16) => {
            if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
                // Clear any existing bounds to ensure centering works
                setMapBounds(null);
                setMapCenter([lat, lng]);
                setMapZoom(zoomLevel);
                console.log(`Centering map on: ${lat}, ${lng}`); // Debug logging
            } else {
                console.warn('Invalid coordinates for map centering:', lat, lng);
            }
        };

        // Handle expansion for worklog items
        if (item.type === 'worklog') {
            const isCurrentlyExpanded = expandedTimelineItem === item.id;
            setExpandedTimelineItem(isCurrentlyExpanded ? null : item.id);
            
            // Handle marker selection and map centering
            const markerId = `worklog-${item.data.id}`;
            setSelectedMarkerId(markerId);
            setOpenPopupId(markerId);
            
            // Center map on worklog location
            if (item.data.location?.coordinates && Array.isArray(item.data.location.coordinates) && item.data.location.coordinates.length >= 2) {
                const [lng, lat] = item.data.location.coordinates; // GeoJSON format [lng, lat]
                centerMapOnLocation(lat, lng);
            } else {
                console.warn('Worklog has no valid location coordinates:', item.data);
            }
            return;
        }
        
        // Clear expansion for non-worklog items
        setExpandedTimelineItem(null);
        
        if (item.type === 'place') {
            const markerId = `place-${item.data.id}`;
            setSelectedMarkerId(markerId);
            setOpenPopupId(markerId);
            
            // Center map on place location
            centerMapOnLocation(item.data.lat, item.data.lng);
            
        } else if (item.type === 'attendance') {
            const markerId = `attendance-${item.subType}`;
            setSelectedMarkerId(markerId);
            setOpenPopupId(markerId);
            
            // Get the appropriate location based on check-in or check-out
            const loc = item.subType === 'check-in' ? item.data.checkInLocation : item.data.checkOutLocation;
            
            // Center map on attendance location
            if (loc?.coordinates && Array.isArray(loc.coordinates) && loc.coordinates.length >= 2) {
                const [lng, lat] = loc.coordinates; // GeoJSON format [lng, lat]
                centerMapOnLocation(lat, lng);
            } else {
                console.warn('Attendance event has no valid location coordinates:', item.data);
            }
        } else if (item.type === 'break') {
            const markerId = `break-${item.subType}-${item.data.id}`;
            setSelectedMarkerId(markerId);
            setOpenPopupId(markerId);
            
            // Get the appropriate location based on start or end
            const loc = item.subType === 'start' ? item.data.startLocation : item.data.endLocation;
            
            // Center map on break location
            if (loc?.lat && loc?.lng && !isNaN(loc.lat) && !isNaN(loc.lng)) {
                centerMapOnLocation(loc.lat, loc.lng);
            } else {
                console.warn('Break event has no valid location coordinates:', item.data);
            }
        } else if (item.type === 'trip') {
            const markerId = `trip-${item.subType}-${item.data.id}`;
            setSelectedMarkerId(markerId);
            setOpenPopupId(null); // Trips don't have popups, they show the trip on map
            
            // Select the trip to show its polyline
            handleTripSelect(item.data.id);
            
            // Center map on trip start or end location
            if (item.subType === 'start' && item.data.origin) {
                centerMapOnLocation(item.data.origin.lat, item.data.origin.lng);
            } else if (item.subType === 'end' && item.data.destination) {
                centerMapOnLocation(item.data.destination.lat, item.data.destination.lng);
            } else {
                // Fallback: show entire trip if specific location not available
                const tripLine = polylines[item.data.id];
                if (tripLine && tripLine.length > 0) {
                    setMapBounds(L.latLngBounds(tripLine));
                }
            }
        } else if (item.type === 'task') {
            const markerId = `task-${item.data.id}`;
            setSelectedMarkerId(markerId);
            setOpenPopupId(markerId);
            // Try to center map on task location if available
            const coords = item?.data?.location?.coordinates;
            if (coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
                centerMapOnLocation(coords.lat, coords.lng);
            } else if (Array.isArray(coords) && coords.length >= 2) {
                const [lng, lat] = coords;
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                    centerMapOnLocation(lat, lng);
                }
            } else if (item?.data?.location && typeof item.data.location === 'object') {
                const lat = item.data.location.lat;
                const lng = item.data.location.lng;
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                    centerMapOnLocation(lat, lng);
                }
            }
        }
    };

    const renderTimelineItem = (item) => {
        let icon, title, details;
        const isExpanded = expandedTimelineItem === item.id;
        const isSelected = isTimelineItemSelected(item);
        
        switch (item.type) {
            case 'attendance':
                icon = <FiClock style={{ color: item.subType === 'check-in' ? '#28a745' : '#dc3545' }} />;
                
                if (item.subType === 'check-in') {
                    title = 'Punched In';
                    const checkInTime = new Date(item.data.checkInTime);
                    const checkOutTime = item.data.checkOutTime ? new Date(item.data.checkOutTime) : null;
                    const now = new Date();
                    
                    // Calculate total time logged in
                    let totalTimeText = '';
                    let isCurrentlyLoggedIn = false;
                    
                    if (checkOutTime) {
                        // Has check-out time
                        if (checkOutTime > now) {
                            // Check-out is in the future - currently logged in
                            isCurrentlyLoggedIn = true;
                            const timeLoggedIn = Math.floor((now - checkInTime) / 1000);
                            totalTimeText = `Currently logged in for: ${formatDuration(timeLoggedIn)}`;
                        } else {
                            // Normal check-out
                            const timeLoggedIn = Math.floor((checkOutTime - checkInTime) / 1000);
                            totalTimeText = `Total time logged: ${formatDuration(timeLoggedIn)}`;
                        }
                    } else {
                        // No check-out time - currently logged in
                        isCurrentlyLoggedIn = true;
                        const timeLoggedIn = Math.floor((now - checkInTime) / 1000);
                        totalTimeText = `Currently logged in for: ${formatDuration(timeLoggedIn)}`;
                    }
                    
                    details = (
                        <>
                            <p><strong>Location:</strong> {item.data.checkInLocation?.address || 'N/A'}</p>
                            {isCurrentlyLoggedIn && (
                                <>
                                    <p><strong>Status:</strong> <span className="attendance-active-status">Currently Active</span></p>
                                </>
                            )}
                            <p className="attendance-time-logged">{totalTimeText}</p>
                            {checkOutTime && checkOutTime <= now && (
                                <p><strong>Checked out:</strong> {moment(checkOutTime).format('hh:mm A')}</p>
                            )}
                        </>
                    );
                } else {
                    title = 'Punched Out';
                    details = <p><strong>Location:</strong> {item.data.checkOutLocation?.address || 'N/A'}</p>;
                }
                break;
            case 'break':
                if (item.subType === 'start') {
                    icon = <FiPauseCircle style={{ color: '#ff9800' }} />;
                    title = `Break Started: ${item.data.reason}`;
                    const breakStartTime = new Date(item.data.startTime);
                    const breakEndTime = item.data.endTime ? new Date(item.data.endTime) : null;
                    const now = new Date();
                    
                    let durationText = '';
                    if (breakEndTime) {
                        durationText = `Duration: ${item.data.duration} hours`;
                    } else {
                        // Break is still ongoing
                        const currentDuration = (now - breakStartTime) / (1000 * 60 * 60); // in hours
                        durationText = `Currently on break for: ${currentDuration.toFixed(2)} hours`;
                    }
                    
                    details = (
                        <>
                            <p><strong>Reason:</strong> {item.data.reason}</p>
                            <p><strong>{durationText}</strong></p>
                            {item.data.startLocation && (
                                <p><strong>Start Location:</strong> {item.data.startLocation.lat.toFixed(6)}, {item.data.startLocation.lng.toFixed(6)}</p>
                            )}
                            {!breakEndTime && (
                                <p><strong>Status:</strong> <span className="break-active-status">Currently on Break</span></p>
                            )}
                        </>
                    );
                } else {
                    icon = <FiPlayCircle style={{ color: '#4caf50' }} />;
                    title = `Break Ended: ${item.data.reason}`;
                    details = (
                        <>
                            <p><strong>Reason:</strong> {item.data.reason}</p>
                            <p><strong>Total Duration:</strong> {item.data.duration} hours</p>
                            {item.data.endLocation && (
                                <p><strong>End Location:</strong> {item.data.endLocation.lat.toFixed(6)}, {item.data.endLocation.lng.toFixed(6)}</p>
                            )}
                        </>
                    );
                }
                break;
            case 'worklog':
                icon = <FiBriefcase style={{ color: '#007bff' }} />; title = `Work Log: ${item.data.Department?.name || 'General'}`;
                details = (
                    <>
                        <p>{item.data.description}</p>
                        <p><strong>Status:</strong> {item.data.status}</p>
                        {item.data.location?.address && <p><strong>Location:</strong> {item.data.location.address}</p>}
                        
                        {isExpanded && (
                            <div className="worklog-expanded-details">
                                {item.data.createdAt && (
                                    <p><strong>Created:</strong> {moment(item.data.createdAt).format('MMMM Do, YYYY at hh:mm A')}</p>
                                )}
                                {item.data.updatedAt && item.data.updatedAt !== item.data.createdAt && (
                                    <p><strong>Updated:</strong> {moment(item.data.updatedAt).format('MMMM Do, YYYY at hh:mm A')}</p>
                                )}
                                {item.data.priority && (
                                    <p><strong>Priority:</strong> {item.data.priority}</p>
                                )}
                                {item.data.notes && (
                                    <p><strong>Notes:</strong> {item.data.notes}</p>
                                )}
                                {item.data.assignedEmployee && (
                                    <p><strong>Assigned to:</strong> {item.data.assignedEmployee.firstName} {item.data.assignedEmployee.lastName}</p>
                                )}
                                {item.data.supervisor && (
                                    <p><strong>Supervisor:</strong> {item.data.supervisor.firstName} {item.data.supervisor.lastName}</p>
                                )}
                                
                                {/* Images */}
                                {item.data.images && item.data.images.length > 0 && (
                                    <div className="worklog-images">
                                        <p><strong>Images:</strong></p>
                                        <div className="timeline-image-grid">
                                            {item.data.images.map((image, index) => (
                                                <img 
                                                    key={index}
                                                    src={image}
                                                    alt={`Work log image ${index + 1}`}
                                                    className="timeline-image"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedImage(image);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Employee Images */}
                                {item.data.employeeImages && item.data.employeeImages.length > 0 && (
                                    <div className="worklog-images">
                                        <p><strong>Employee Images:</strong></p>
                                        <div className="timeline-image-grid">
                                            {item.data.employeeImages.map((image, index) => (
                                                <img 
                                                    key={index}
                                                    src={image}
                                                    alt={`Employee image ${index + 1}`}
                                                    className="timeline-image"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedImage(image);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                );
                break;
            case 'place':
                icon = <FiMapPin style={{ color: '#ffc107' }} />; title = `Visited: ${item.data.placeName}`;
                details = <><p><strong>Address:</strong> {item.data.placeAddress}</p><p><strong>Duration:</strong> {formatDuration(item.data.duration * 60)}</p></>;
                break;
            case 'trip':
                if (item.subType === 'start') {
                    const TripTypeIcon = getTripTypeIcon(item.data.tripType);
                    icon = <TripTypeIcon style={{ color: '#28a745' }} />;
                    title = `Trip Started`;
                    details = (
                        <>
                            <p><strong>From:</strong> {item.data.origin?.placeAddress || 'Unknown location'}</p>
                            <p><strong>Trip Type:</strong> {item.data.tripType.charAt(0).toUpperCase() + item.data.tripType.slice(1)}</p>
                        </>
                    );
                } else {
                    icon = <FiFlag style={{ color: '#dc3545' }} />;
                    title = `Trip Ended`;
                    details = (
                        <>
                            <p><strong>To:</strong> {item.data.destination?.placeAddress || 'Unknown location'}</p>
                            <p><strong>Distance:</strong> {item.data.stats?.distance?.toFixed(2)} km</p>
                            <p><strong>Duration:</strong> {formatDuration(item.data.stats?.duration * 60)}</p>
                            <p><strong>Avg Speed:</strong> {item.data.stats?.averageSpeed?.toFixed(1)} km/h</p>
                        </>
                    );
                }
                break;
            case 'task':
                if (item.subType === 'started') {
                    icon = <FiPlayCircle style={{ color: '#0ea5e9' }} />;
                    title = `Task Started`;
                } else {
                    icon = <FiFlag style={{ color: '#10b981' }} />;
                    title = `Task Resolved`;
                }
                // Compute helpers for mini-cards
                const startedAt = item.data?.startedAt ? new Date(item.data.startedAt) : (item.subType === 'started' ? item.timestamp : null);
                const resolvedAt = item.data?.resolvedAt ? new Date(item.data.resolvedAt) : (item.subType === 'resolved' ? item.timestamp : null);
                const durationSec = (startedAt && resolvedAt && resolvedAt >= startedAt) ? Math.floor((resolvedAt - startedAt) / 1000) : null;
                const isLastItem = timelineData.length > 0 && timelineData[timelineData.length - 1]?.id === item.id;
                const now = new Date();
                const ongoingDurationSec = (item.subType === 'started' && isLastItem && !item.data?.resolvedAt && startedAt)
                    ? Math.max(0, Math.floor((now - startedAt) / 1000))
                    : null;

                details = (
                    <>
                        {/* Inline mini cards for task context */}
                        {item.subType === 'resolved' && (
                            <div style={{ marginBottom: 6, padding: '6px 8px', background: '#f6fffb', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 6, fontSize: 12, color: '#065f46' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#065f46' }}>
                                    <FiFlag style={{ color: '#10b981' }} /> <span>Task resolved</span>
                                </div>
                                <div style={{ marginTop: 2 }}>
                                    <span>Started: {startedAt ? moment(startedAt).format('MMM D, hh:mm A') : '-'}</span>
                                    {durationSec != null && <span style={{ marginLeft: 10 }}>Duration: {formatDuration(durationSec)}</span>}
                                </div>
                            </div>
                        )}
                        {item.subType === 'started' && item.data?.resolvedAt && (
                            <div style={{ marginBottom: 6, padding: '6px 8px', background: '#f6fffb', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 6, fontSize: 12, color: '#065f46' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#065f46' }}>
                                    <FiFlag style={{ color: '#10b981' }} /> <span>Task resolved</span>
                                </div>
                                <div style={{ marginTop: 2 }}>
                                    <span>Resolved: {resolvedAt ? moment(resolvedAt).format('MMM D, hh:mm A') : '-'}</span>
                                    {durationSec != null && <span style={{ marginLeft: 10 }}>Duration: {formatDuration(durationSec)}</span>}
                                </div>
                            </div>
                        )}
                        {ongoingDurationSec != null && (
                            <div style={{ marginBottom: 6, padding: '6px 8px', background: 'var(--light-color)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, fontSize: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                                    <FiPlayCircle style={{ color: '#0ea5e9' }} /> <span>Task ongoing</span>
                                </div>
                                <div style={{ marginTop: 2, color: 'var(--secondary-color)' }}>
                                    <span>Started: {startedAt ? moment(startedAt).format('hh:mm A') : '-'}</span>
                                    <span style={{ marginLeft: 10 }}>Duration: {formatDuration(ongoingDurationSec)}</span>
                                </div>
                            </div>
                        )}

                        {/* Standard task details */}
                        <p><strong>Description:</strong> {item.data.description}</p>
                        {item.data.department?.name && (
                            <p><strong>Department:</strong> {item.data.department.name}</p>
                        )}
                        {item.data.location?.address && (
                            <p><strong>Location:</strong> {item.data.location.address}</p>
                        )}
                    </>
                );
                break;
            default: return null;
        }

        return (
            <div key={item.id} className={`timeline-item timeline-item-${item.type} ${isExpanded ? 'expanded' : ''} ${isSelected ? 'selected' : ''}`} onClick={() => handleTimelineItemClick(item)}>
                <div className="timeline-dot"></div>
                <div className="timeline-item-header">
                    <h3 className="timeline-item-title">
                        {icon} {title}
                        {item.type === 'worklog' && (
                            <span className="expand-indicator">
                                {isExpanded ? '-' : '+'}
                            </span>
                        )}
                    </h3>
                    <span className="timeline-item-time">{moment(item.timestamp).format('hh:mm A')}</span>
                </div>
                <div className="timeline-item-body">{details}</div>
            </div>
        );
    };

    const selectedTrip = trips.find(trip => trip.id === selectedTripId);

    // --- Team View (no employee selected) ---
    const renderTeamLocationList = () => (
        <>
            <div className="location-list-header">
                <h3>Team Locations</h3>
                <span className="location-count-pill">{employeeLocations.length}</span>
            </div>
            <div className="location-list">
                {employeeLocations.map((item, index) => {
                    if (!item.location || !item.location.coords) return null;
                    const employee = item.employee;
                    const lastUpdated = new Date(item.lastUpdated);
                    const formattedDate = lastUpdated.toLocaleString();
                    const isStale = (new Date() - lastUpdated) > (60 * 60 * 1000);
                    const isSelected = Boolean(selectedTeamLocation?.employee?.id && selectedTeamLocation.employee.id === employee.id);
                    const placeName = placeNames[`${item.location.coords.latitude},${item.location.coords.longitude}`];
                    const batteryLevel = item.location.batteryLevel;
                    const accuracy = item.location.accuracy;

                    const handleCardClick = () => {
                        setSelectedTeamLocation(item);
                        if (teamMapRef.current && teamMarkersRef.current[employee.id]) {
                            teamMapRef.current.setView(
                                [item.location.coords.latitude, item.location.coords.longitude],
                                16
                            );
                            setTimeout(() => {
                                teamMarkersRef.current[employee.id].openPopup();
                            }, 100);
                        }
                    };

                    return (
                        <div 
                            key={index} 
                            className={`location-card ${isStale ? 'stale' : ''} ${isSelected ? 'selected' : ''}`}
                            onClick={handleCardClick}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleCardClick();
                                }
                            }}
                        >
                            <div className="location-info">
                                <div className="location-card-header">
                                    <div className="location-avatar">
                                        {employee.profilePicture ? (
                                            <img src={employee.profilePicture} alt={`${employee.firstName} ${employee.lastName}`} />
                                        ) : (
                                            `${employee.firstName.charAt(0)}${employee.lastName.charAt(0)}`
                                        )}
                                    </div>
                                    <div className="location-card-title">
                                        <div className="location-name-row">
                                            <h4 className="location-name">{employee.firstName} {employee.lastName}</h4>
                                            {isStale && (
                                                <span className="location-pill location-pill-warning">Stale</span>
                                            )}
                                        </div>
                                        <p className="location-address">
                                            {placeName || 'Loading address...'}
                                        </p>
                                    </div>
                                </div>

                                <div className="location-metrics-row">
                                    <div className="location-metric battery" style={{ '--battery-color': getBatteryColor(batteryLevel) }}>
                                        <i className="fas fa-battery-full" style={{ marginRight: '0.25rem', fontSize: '0.9em' }}></i>
                                        <span className="metric-value">
                                            {batteryLevel ? `${batteryLevel}%` : 'N/A'}
                                        </span>
                                    </div>
                                    <div className="location-metric accuracy" style={{ '--accuracy-color': getAccuracyColor(accuracy) }}>
                                        <i className="fas fa-map-marker-alt" style={{ marginRight: '0.25rem', fontSize: '0.9em' }}></i>
                                        <span className="metric-value" >
                                            {accuracy ? `${Math.round(accuracy)}m` : 'N/A'}
                                        </span>
                                    </div>
                                </div>

                                <div className="location-meta-row">
                                    <span className="location-time">Updated {formattedDate}</span>
                                    <span className="location-pill location-pill-neutral">View on map</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );

    const renderTeamMap = () => {
        if (employeeLocations.length === 0) {
            return <div className="no-data-message">No field staff locations available</div>;
        }

        const validLocations = employeeLocations.filter(item => 
            item.location && item.location.coords
        );
        if (validLocations.length === 0) {
            return <div className="no-data-message">No valid location data available</div>;
        }

        const bounds = validLocations.reduce((b, item) => {
            return b.extend([
                item.location.coords.latitude,
                item.location.coords.longitude
            ]);
        }, L.latLngBounds([])).pad(0.1);

        return (
            <MapContainer 
                key={`team-map-${validLocations.length}`}
                bounds={bounds}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%' }}
                whenCreated={mapInstance => { 
                    teamMapRef.current = mapInstance;
                    setTimeout(() => {
                        mapInstance.invalidateSize();
                    }, 100);
                }}
            >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {validLocations.map((item, index) => {
                    const employee = item.employee;
                    const lastUpdated = new Date(item.lastUpdated);
                    const isStale = (new Date() - lastUpdated) > (60 * 60 * 1000);
                    const placeName = placeNames[`${item.location.coords.latitude},${item.location.coords.longitude}`];
                    const initials = `${employee.firstName.charAt(0)}${employee.lastName.charAt(0)}`;
                    const isSelected = Boolean(selectedTeamLocation?.employee?.id && selectedTeamLocation.employee.id === employee.id);
                    const batteryLevel = item.location.batteryLevel;
                    const accuracy = item.location.accuracy;
                    const popupId = `team-${employee.id}`;
                    
                    return (
                        <Marker 
                            key={`${employee.id}-${index}`}
                            position={[item.location.coords.latitude, item.location.coords.longitude]}
                            icon={createAvatarIcon(employee.profilePicture, initials, isStale, isSelected)}
                            className={isStale ? 'stale' : ''}
                            ref={marker => {
                                if (marker) {
                                    teamMarkersRef.current[employee.id] = marker;
                                }
                            }}
                            eventHandlers={{
                                click: () => {
                                    setSelectedTeamLocation(item);
                                    teamMapRef.current?.setView(
                                        [item.location.coords.latitude, item.location.coords.longitude],
                                        16
                                    );
                                    setOpenPopupId(popupId);
                                }
                            }}
                        >
                            <Popup
                                className="location-popup-card"
                                autoPan={true}
                                closeButton={true}
                                markerId={popupId}
                            >
                                {renderLocationMarkerCard({
                                    employee,
                                    coords: item.location.coords,
                                    placeName,
                                    batteryLevel,
                                    accuracy,
                                    lastUpdated: item.lastUpdated,
                                    isStale,
                                    title: 'Current'
                                })}
                            </Popup>
                        </Marker>
                    );
                })}
                <PopupController openPopupId={openPopupId} />
            </MapContainer>
        );
    };

    return (
        <div className="page-container" style={{ background: '#f8fafc', minHeight: '100vh' }}>
            <div className="page-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                    <h2 style={{ fontSize: 20, margin: 0, color: '#111827', fontWeight: 600 }}>
                        {locationViewMode === 'vehicles' ? 'Fleet Live Location' : 'Field Staff Live Location'}
                    </h2>
                    <div className="toolbar-compact">
                        <div className="toolbar-control">
                            <button
                                className={`input-compact ${locationViewMode === 'employees' ? 'active' : ''}`}
                                style={{ marginRight: 8 }}
                                onClick={() => { setLocationViewMode('employees'); setSelectedVehicle(''); }}
                            >
                                Employees
                            </button>
                            <button
                                className={`input-compact ${locationViewMode === 'vehicles' ? 'active' : ''}`}
                                onClick={() => { setLocationViewMode('vehicles'); setSelectedEmployee(''); }}
                            >
                                Vehicles
                            </button>
                        </div>
                        {locationViewMode === 'employees' ? (
                        <>
                        <div className="toolbar-control">
                            <FiUser className="toolbar-icon" />
                            <select
                                id="employee-select"
                                className="input-compact"
                                value={selectedEmployee}
                                onChange={e => setSelectedEmployee(e.target.value)}
                            >
                                <option value="">Select employee...</option>
                                {employees.map(emp => (
                                    <option key={emp.id} value={emp.id}>{`${emp.firstName} ${emp.lastName}`}</option>
                                ))}
                            </select>
                        </div>
                        {selectedEmployee ? (
                        <div className="toolbar-control">
                            <button
                                className={`input-compact ${employeeLocationSource === 'phone' ? 'active' : ''}`}
                                onClick={() => setEmployeeLocationSource('phone')}
                            >
                                Phone
                            </button>
                            <button
                                className={`input-compact ${employeeLocationSource === 'tracker' ? 'active' : ''}`}
                                style={{ marginLeft: 8 }}
                                onClick={() => setEmployeeLocationSource('tracker')}
                            >
                                Personal tracker
                            </button>
                        </div>
                        ) : null}
                        </>
                        ) : (
                        <div className="toolbar-control">
                            <FiTruck className="toolbar-icon" />
                            <select
                                id="vehicle-select"
                                className="input-compact"
                                value={selectedVehicle}
                                onChange={e => setSelectedVehicle(e.target.value)}
                            >
                                <option value="">Select vehicle...</option>
                                {vehicles.map((vehicle) => (
                                    <option key={vehicle.id} value={vehicle.id}>{vehicle.name}</option>
                                ))}
                            </select>
                        </div>
                        )}
                        <div className={`toolbar-control ${!(locationViewMode === 'employees' ? selectedEmployee : selectedVehicle) ? 'disabled' : ''}`}>
                            <FiCalendar className="toolbar-icon" />
                            <input
                                id="date-select"
                                className="input-compact"
                                type="date"
                                value={selectedDate}
                                onChange={e => setSelectedDate(e.target.value)}
                                max={moment().format('YYYY-MM-DD')}
                                disabled={!(locationViewMode === 'employees' ? selectedEmployee : selectedVehicle)}
                                title={!(locationViewMode === 'employees' ? selectedEmployee : selectedVehicle) ? 'Select an item to choose a date' : undefined}
                            />
                        </div>
                    </div>
                </div>
            
            <div className="location-page-content">
                <div className="location-content">
                    {isLoading ? (
                        <div className="loading-state">
                            <FaSpinner className="loading-spinner" />
                            <h3>Loading Location Data...</h3>
                            <p>Fetching employee activity for {moment(selectedDate).format('MMMM Do, YYYY')}</p>
                        </div>
                    ) : !(locationViewMode === 'employees' ? selectedEmployee : selectedVehicle) ? (
                        <div className="location-container" style={{ gridColumn:'1 / -1', display:'grid', gridTemplateColumns:'minmax(480px, 1fr) 380px', gap:16, width:'100%', alignItems:'stretch' }}>
                            <div className="map-container-dashboard">
                                {renderTeamMap()}
                            </div>
                            <div className="location-list-container">
                                {renderTeamLocationList()}
                            </div>
                        </div>
                    ) : timelineData.length === 0 && trips.length === 0 ? (
                        latestLocation && latestLocation.location?.coords ? (
                            // Show map with just latest location if no historical data but latest location available
                            <>
                                <div className="map-column">
                                    <div className="map-view-container">
                                        <MapContainer 
                                            center={mapCenter} 
                                            zoom={mapZoom} 
                                            className="map-container"
                                            eventHandlers={{
                                                click: () => handleDeselect()
                                            }}
                                        >
                                            <MapUpdater center={mapCenter} zoom={mapZoom} bounds={mapBounds} />
                                            <PopupController openPopupId={openPopupId} />
                                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
                                            
                                            {/* Latest Location Marker */}
                                            <Marker 
                                                position={[latestLocation.location.coords.latitude, latestLocation.location.coords.longitude]}
                                                icon={createAvatarIcon(
                                                    latestLocation.employee.profilePicture,
                                                    `${latestLocation.employee.firstName.charAt(0)}${latestLocation.employee.lastName.charAt(0)}`,
                                                    (new Date() - new Date(latestLocation.lastUpdated)) > (60 * 60 * 1000), // isStale if > 1 hour
                                                    selectedMarkerId === 'latest-location'
                                                )}
                                                eventHandlers={{
                                                    click: (e) => {
                                                        e.originalEvent.stopPropagation();
                                                        setSelectedMarkerId('latest-location');
                                                        setExpandedTimelineItem(null);
                                                        setOpenPopupId('latest-location');
                                                        // Clear bounds and center map on latest location
                                                        setMapBounds(null);
                                                        setMapCenter([latestLocation.location.coords.latitude, latestLocation.location.coords.longitude]);
                                                        setMapZoom(16);
                                                    },
                                                    popupclose: () => {
                                                        if (openPopupId === 'latest-location') setOpenPopupId(null);
                                                    }
                                                }}
                                            >
                                                <Popup className="location-popup-card" markerId="latest-location" closeButton={true}>
                                                    {renderLocationMarkerCard({
                                                        employee: latestLocation.employee,
                                                        coords: latestLocation.location.coords,
                                                        placeName: placeNames[`${latestLocation.location.coords.latitude},${latestLocation.location.coords.longitude}`],
                                                        batteryLevel: latestLocation.location.batteryLevel,
                                                        accuracy: latestLocation.location.accuracy,
                                                        lastUpdated: latestLocation.lastUpdated,
                                                        isStale: (new Date() - new Date(latestLocation.lastUpdated)) > (60 * 60 * 1000),
                                                        title: 'Last known'
                                                    })}
                                                </Popup>
                                            </Marker>
                                        </MapContainer>
                                    </div>
                                </div>
                                <div className="timeline-column">
                                    <div className="timeline-container">
                                        <div className="empty-state" style={{ height: '100%', margin: 0 }}>
                                            <FaExclamationTriangle className="empty-icon" />
                                            <h3>No Activity Data</h3>
                                            <p>No trips or work logs were recorded for {moment(selectedDate).format('MMMM Do, YYYY')}, but the employee's last known location is shown on the map.</p>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="empty-state">
                                <FaExclamationTriangle className="empty-icon" />
                                <h3>No Activity Found</h3>
                                <p>No location data, trips, or work logs were recorded for {moment(selectedDate).format('MMMM Do, YYYY')}.</p>
                            </div>
                        )
                    ) : (
                        <>
                            <div className="map-column">
                                <div className="map-view-container">
                                    <MapContainer 
                                        center={mapCenter} 
                                        zoom={mapZoom} 
                                        className="map-container"
                                        eventHandlers={{
                                            click: () => handleDeselect()
                                        }}
                                    >
                                        <MapUpdater center={mapCenter} zoom={mapZoom} bounds={mapBounds} />
                                        <PopupController openPopupId={openPopupId} />
                                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
                                        
                                        {trips.map(trip => (
                                            <Polyline key={trip.id} positions={polylines[trip.id]} pathOptions={{ color: selectedTripId === trip.id ? '#000' : '#6c757d', weight: selectedTripId === trip.id ? 3 : 2, opacity: selectedTripId === trip.id ? 1 : 0.7 }} />
                                        ))}
                                        {/* Task Markers (started/resolved on selected day) */}
                                        {tasks && tasks.map(t => {
                                            const coords = t?.location?.coordinates;
                                            let lat = null, lng = null;
                                            if (coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
                                                lat = coords.lat; lng = coords.lng;
                                            } else if (Array.isArray(coords) && coords.length >= 2) {
                                                // GeoJSON [lng, lat]
                                                lng = coords[0]; lat = coords[1];
                                            } else if (t?.location && Number.isFinite(t.location.lat) && Number.isFinite(t.location.lng)) {
                                                lat = t.location.lat; lng = t.location.lng;
                                            }
                                            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                                            const color = String(t.status).toLowerCase() === 'resolved' ? '#10b981' : '#0ea5e9';
                                            const markerId = `task-${t.id}`;
                                            const iconClass = String(t.status).toLowerCase() === 'resolved' ? 'fas fa-check' : 'fas fa-play';
                                            return (
                                                <Marker
                                                    key={markerId}
                                                    position={[lat, lng]}
                                                    icon={createCircularIcon(color, markerId, { iconClass, type: 'task', title: `Task: ${t.description || ''}` })}
                                                    eventHandlers={{
                                                        click: (e) => {
                                                            e.originalEvent.stopPropagation();
                                                            setSelectedMarkerId(markerId);
                                                            setOpenPopupId(markerId);
                                                            setExpandedTimelineItem(null);
                                                            setMapBounds(null);
                                                            setMapCenter([lat, lng]);
                                                            setMapZoom(16);
                                                        },
                                                        popupclose: () => {
                                                            if (openPopupId === markerId) {
                                                                setOpenPopupId(null);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <Popup 
                                                        autoClose={false}
                                                        closeOnClick={false}
                                                        markerId={markerId}
                                                        {...(openPopupId === markerId ? { autoClose: false, closeButton: true } : {})}
                                                    >
                                                        <b>Task</b><br/>{t.description}<br/><i>Status: {t.status}</i>
                                                    </Popup>
                                                </Marker>
                                            );
                                        })}

                                        {/* Markers for selected trip start/end */}
                                        {selectedTrip && polylines[selectedTrip.id] && polylines[selectedTrip.id].length > 0 && (
                                            <>
                                                <Marker
                                                    position={polylines[selectedTrip.id][0]}
                                                    icon={createCircularIcon('#22c55e', `trip-start-${selectedTrip.id}`, { iconClass: 'fas fa-play', type: 'trip', title: 'Trip start' })}
                                                    eventHandlers={{
                                                        click: (e) => {
                                                            e.originalEvent.stopPropagation();
                                                            const markerId = `trip-start-${selectedTrip.id}`;
                                                            setSelectedMarkerId(markerId);
                                                            setOpenPopupId(markerId);
                                                            setExpandedTimelineItem(null);
                                                        },
                                                        popupclose: () => {
                                                            if (openPopupId === `trip-start-${selectedTrip.id}`) setOpenPopupId(null);
                                                        }
                                                    }}
                                                >
                                                    <Popup autoClose={false} closeOnClick={false} markerId={`trip-start-${selectedTrip.id}`}>
                                                        <b>Trip Start:</b> {moment(selectedTrip.startTime).format('hh:mm A')}
                                                    </Popup>
                                                </Marker>
                                                <Marker
                                                    position={polylines[selectedTrip.id][polylines[selectedTrip.id].length - 1]}
                                                    icon={createCircularIcon('#ef4444', `trip-end-${selectedTrip.id}`, { iconClass: 'fas fa-flag-checkered', type: 'trip', title: 'Trip end' })}
                                                    eventHandlers={{
                                                        click: (e) => {
                                                            e.originalEvent.stopPropagation();
                                                            const markerId = `trip-end-${selectedTrip.id}`;
                                                            setSelectedMarkerId(markerId);
                                                            setOpenPopupId(markerId);
                                                            setExpandedTimelineItem(null);
                                                        },
                                                        popupclose: () => {
                                                            if (openPopupId === `trip-end-${selectedTrip.id}`) setOpenPopupId(null);
                                                        }
                                                    }}
                                                >
                                                    <Popup autoClose={false} closeOnClick={false} markerId={`trip-end-${selectedTrip.id}`}>
                                                        <b>Trip End:</b> {moment(selectedTrip.endTime).format('hh:mm A')}
                                                    </Popup>
                                                </Marker>
                                            </>
                                        )}

                                        {visitedPlaces.map(p => (
                                            <Marker 
                                                key={p.id} 
                                                position={[p.lat, p.lng]} 
                                                icon={createCircularIcon('#f59e0b', `place-${p.id}`, { iconClass: 'fas fa-map-marker-alt', type: 'place', title: p.placeName || 'Visited place' })}
                                                eventHandlers={{
                                                    click: (e) => {
                                                        e.originalEvent.stopPropagation();
                                                        const markerId = `place-${p.id}`;
                                                        setSelectedMarkerId(markerId);
                                                        setOpenPopupId(markerId);
                                                        setExpandedTimelineItem(null); // Clear timeline expansion when clicking markers
                                                        // Clear bounds and center map on marker location
                                                        setMapBounds(null);
                                                        setMapCenter([p.lat, p.lng]);
                                                        setMapZoom(16);
                                                    },
                                                    popupclose: () => {
                                                        if (openPopupId === `place-${p.id}`) {
                                                            setOpenPopupId(null);
                                                        }
                                                    }
                                                }}
                                            >
                                                <Popup 
                                                    autoClose={false}
                                                    closeOnClick={false}
                                                    markerId={`place-${p.id}`}
                                                    {...(openPopupId === `place-${p.id}` ? { autoClose: false, closeButton: true } : {})}
                                                >
                                                    <b>{p.placeName}</b><br/>{p.placeAddress}<br/>Duration: {formatDuration(p.duration*60)}
                                                </Popup>
                                            </Marker>
                                        ))}
                                        {filteredWorkLogs.filter(w => w.location?.coordinates).map(w => (
                                            <Marker 
                                                key={`wl-${w.id}`} 
                                                position={[w.location.coordinates[1], w.location.coordinates[0]]} 
                                                icon={createCircularIcon('#3b82f6', `worklog-${w.id}`, { iconClass: 'fas fa-briefcase', type: 'worklog', title: 'Work log' })}
                                                eventHandlers={{
                                                    click: (e) => {
                                                        e.originalEvent.stopPropagation();
                                                        const markerId = `worklog-${w.id}`;
                                                        setSelectedMarkerId(markerId);
                                                        setOpenPopupId(markerId);
                                                        setExpandedTimelineItem(null); // Clear timeline expansion when clicking markers
                                                        // Clear bounds and center map on marker location
                                                        setMapBounds(null);
                                                        setMapCenter([w.location.coordinates[1], w.location.coordinates[0]]);
                                                        setMapZoom(16);
                                                    },
                                                    popupclose: () => {
                                                        if (openPopupId === `worklog-${w.id}`) {
                                                            setOpenPopupId(null);
                                                        }
                                                    }
                                                }}
                                            >
                                                <Popup 
                                                    autoClose={false}
                                                    closeOnClick={false}
                                                    markerId={`worklog-${w.id}`}
                                                    {...(openPopupId === `worklog-${w.id}` ? { autoClose: false, closeButton: true } : {})}
                                                >
                                                    <b>Work Log</b><br/>{w.description}
                                                </Popup>
                                            </Marker>
                                        ))}
                                        {attendanceData?.checkInLocation && (
                                            <Marker 
                                                position={[attendanceData.checkInLocation.coordinates[1], attendanceData.checkInLocation.coordinates[0]]} 
                                                icon={createCircularIcon('#22c55e', 'attendance-check-in', { iconClass: 'fas fa-sign-in-alt', type: 'attendance', title: 'Punched in' })}
                                                eventHandlers={{
                                                    click: (e) => {
                                                        e.originalEvent.stopPropagation();
                                                        setSelectedMarkerId('attendance-check-in');
                                                        setOpenPopupId('attendance-check-in');
                                                        setExpandedTimelineItem(null); // Clear timeline expansion when clicking markers
                                                        // Clear bounds and center map on marker location
                                                        setMapBounds(null);
                                                        setMapCenter([attendanceData.checkInLocation.coordinates[1], attendanceData.checkInLocation.coordinates[0]]);
                                                        setMapZoom(16);
                                                    },
                                                    popupclose: () => {
                                                        if (openPopupId === 'attendance-check-in') {
                                                            setOpenPopupId(null);
                                                        }
                                                    }
                                                }}
                                            >
                                                <Popup 
                                                    autoClose={false}
                                                    closeOnClick={false}
                                                    markerId="attendance-check-in"
                                                    {...(openPopupId === 'attendance-check-in' ? { autoClose: false, closeButton: true } : {})}
                                                >
                                                    <b>Punched In</b><br/>{attendanceData.checkInLocation.address}
                                                </Popup>
                                            </Marker>
                                        )}
                                        {attendanceData?.checkOutLocation && (
                                            <Marker 
                                                position={[attendanceData.checkOutLocation.coordinates[1], attendanceData.checkOutLocation.coordinates[0]]} 
                                                icon={createCircularIcon('#ef4444', 'attendance-check-out', { iconClass: 'fas fa-sign-out-alt', type: 'attendance', title: 'Punched out' })}
                                                eventHandlers={{
                                                    click: (e) => {
                                                        e.originalEvent.stopPropagation();
                                                        setSelectedMarkerId('attendance-check-out');
                                                        setOpenPopupId('attendance-check-out');
                                                        setExpandedTimelineItem(null); // Clear timeline expansion when clicking markers
                                                        // Clear bounds and center map on marker location
                                                        setMapBounds(null);
                                                        setMapCenter([attendanceData.checkOutLocation.coordinates[1], attendanceData.checkOutLocation.coordinates[0]]);
                                                        setMapZoom(16);
                                                    },
                                                    popupclose: () => {
                                                        if (openPopupId === 'attendance-check-out') {
                                                            setOpenPopupId(null);
                                                        }
                                                    }
                                                }}
                                            >
                                                <Popup 
                                                    autoClose={false}
                                                    closeOnClick={false}
                                                    markerId="attendance-check-out"
                                                    {...(openPopupId === 'attendance-check-out' ? { autoClose: false, closeButton: true } : {})}
                                                >
                                                    <b>Punched Out</b><br/>{attendanceData.checkOutLocation.address}
                                                </Popup>
                                            </Marker>
                                        )}
                                        
                                        {/* Break Markers */}
                                        {attendanceData?.breaks && attendanceData.breaks.map((breakRecord) => (
                                            <React.Fragment key={`break-${breakRecord.id}`}>
                                                {/* Break Start Marker */}
                                                {breakRecord.startLocation && (
                                                    <Marker 
                                                        position={[breakRecord.startLocation.lat, breakRecord.startLocation.lng]} 
                                                        icon={createCircularIcon('#f97316', `break-start-${breakRecord.id}`, { iconClass: 'fas fa-pause', type: 'break', title: `Break started: ${breakRecord.reason || ''}` })}
                                                        eventHandlers={{
                                                            click: (e) => {
                                                                e.originalEvent.stopPropagation();
                                                                setSelectedMarkerId(`break-start-${breakRecord.id}`);
                                                                setOpenPopupId(`break-start-${breakRecord.id}`);
                                                                setExpandedTimelineItem(null);
                                                                setMapBounds(null);
                                                                setMapCenter([breakRecord.startLocation.lat, breakRecord.startLocation.lng]);
                                                                setMapZoom(16);
                                                            },
                                                            popupclose: () => {
                                                                if (openPopupId === `break-start-${breakRecord.id}`) {
                                                                    setOpenPopupId(null);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Popup 
                                                            autoClose={false}
                                                            closeOnClick={false}
                                                            markerId={`break-start-${breakRecord.id}`}
                                                            {...(openPopupId === `break-start-${breakRecord.id}` ? { autoClose: false, closeButton: true } : {})}
                                                        >
                                                            <b>Break Started: {breakRecord.reason}</b><br/>
                                                            Duration: {breakRecord.duration} hours<br/>
                                                            {moment(breakRecord.startTime).format('hh:mm A')}
                                                        </Popup>
                                                    </Marker>
                                                )}
                                                
                                                {/* Break End Marker */}
                                                {breakRecord.endLocation && breakRecord.endTime && (
                                                    <Marker 
                                                        position={[breakRecord.endLocation.lat, breakRecord.endLocation.lng]} 
                                                        icon={createCircularIcon('#10b981', `break-end-${breakRecord.id}`, { iconClass: 'fas fa-play', type: 'break', title: `Break ended: ${breakRecord.reason || ''}` })}
                                                        eventHandlers={{
                                                            click: (e) => {
                                                                e.originalEvent.stopPropagation();
                                                                setSelectedMarkerId(`break-end-${breakRecord.id}`);
                                                                setOpenPopupId(`break-end-${breakRecord.id}`);
                                                                setExpandedTimelineItem(null);
                                                                setMapBounds(null);
                                                                setMapCenter([breakRecord.endLocation.lat, breakRecord.endLocation.lng]);
                                                                setMapZoom(16);
                                                            },
                                                            popupclose: () => {
                                                                if (openPopupId === `break-end-${breakRecord.id}`) {
                                                                    setOpenPopupId(null);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Popup 
                                                            autoClose={false}
                                                            closeOnClick={false}
                                                            markerId={`break-end-${breakRecord.id}`}
                                                            {...(openPopupId === `break-end-${breakRecord.id}` ? { autoClose: false, closeButton: true } : {})}
                                                        >
                                                            <b>Break Ended: {breakRecord.reason}</b><br/>
                                                            Total Duration: {breakRecord.duration} hours<br/>
                                                            {moment(breakRecord.endTime).format('hh:mm A')}
                                                        </Popup>
                                                    </Marker>
                                                )}
                                            </React.Fragment>
                                        ))}
                                        
                                        {/* Latest Location Marker */}
                                        {latestLocation && latestLocation.location?.coords && (
                                            <Marker 
                                                position={[latestLocation.location.coords.latitude, latestLocation.location.coords.longitude]}
                                                icon={createAvatarIcon(
                                                    latestLocation.employee.profilePicture,
                                                    `${latestLocation.employee.firstName.charAt(0)}${latestLocation.employee.lastName.charAt(0)}`,
                                                    (new Date() - new Date(latestLocation.lastUpdated)) > (60 * 60 * 1000), // isStale if > 1 hour
                                                    selectedMarkerId === 'latest-location'
                                                )}
                                                eventHandlers={{
                                                    click: (e) => {
                                                        e.originalEvent.stopPropagation();
                                                        setSelectedMarkerId('latest-location');
                                                        setExpandedTimelineItem(null);
                                                        setOpenPopupId('latest-location');
                                                        // Clear bounds and center map on latest location
                                                        setMapBounds(null);
                                                        setMapCenter([latestLocation.location.coords.latitude, latestLocation.location.coords.longitude]);
                                                        setMapZoom(16);
                                                    },
                                                    popupclose: () => {
                                                        if (openPopupId === 'latest-location') setOpenPopupId(null);
                                                    }
                                                }}
                                            >
                                                <Popup className="location-popup-card" markerId="latest-location" closeButton={true}>
                                                    {renderLocationMarkerCard({
                                                        employee: latestLocation.employee,
                                                        coords: latestLocation.location.coords,
                                                        placeName: placeNames[`${latestLocation.location.coords.latitude},${latestLocation.location.coords.longitude}`],
                                                        batteryLevel: latestLocation.location.batteryLevel,
                                                        accuracy: latestLocation.location.accuracy,
                                                        lastUpdated: latestLocation.lastUpdated,
                                                        isStale: (new Date() - new Date(latestLocation.lastUpdated)) > (60 * 60 * 1000),
                                                        title: 'Last known'
                                                    })}
                                                </Popup>
                                            </Marker>
                                        )}
                                    </MapContainer>
                                    
                                    {/* Trip selector overlay */}
                                    {trips.length > 0 && (
                                        <div className="trip-selector-overlay">
                                            {trips.map((trip, index) => ( 
                                                <button key={trip.id} className={`trip-button ${selectedTripId === trip.id ? 'selected' : ''}`} onClick={() => handleTripSelect(trip.id)}> 
                                                    Trip {index + 1} 
                                                </button> 
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Horizontal Trip Details at Bottom */}
                                    {selectedTrip && (
                                        <div className={`trip-details-bottom ${isTripDetailsCollapsed ? 'collapsed' : ''}`}>
                                            <div className="trip-details-header-horizontal">
                                                <div className="trip-details-title">
                                                    {React.createElement(getTripTypeIcon(selectedTrip.tripType))}
                                                    <span>Trip Details</span>
                                                </div>
                                                <button 
                                                    className="collapse-toggle"
                                                    onClick={() => setIsTripDetailsCollapsed(!isTripDetailsCollapsed)}
                                                >
                                                    {isTripDetailsCollapsed ? <FaChevronUp /> : <FaChevronDown />}
                                                </button>
                                            </div>
                                            
                                            {!isTripDetailsCollapsed && (
                                                <div className="trip-details-content-horizontal">
                                                    <div className="trip-stats-horizontal">
                                                        <div className="stat-item-horizontal">
                                                            <span className="stat-label">Distance</span>
                                                            <span className="stat-value">{selectedTrip.stats.distance.toFixed(2)} km</span>
                                                        </div>
                                                        <div className="stat-item-horizontal">
                                                            <span className="stat-label">Duration</span>
                                                            <span className="stat-value">{formatDuration(selectedTrip.stats.duration * 60)}</span>
                                                        </div>
                                                        <div className="stat-item-horizontal">
                                                            <span className="stat-label">Avg Speed</span>
                                                            <span className="stat-value">{selectedTrip.stats.averageSpeed.toFixed(1)} km/h</span>
                                                        </div>
                                                        <div className="stat-item-horizontal">
                                                            <span className="stat-label">Max Speed</span>
                                                            <span className="stat-value">{selectedTrip.stats.maxSpeed.toFixed(1)} km/h</span>
                                                        </div>
                                                    </div>
                                                    <div className="trip-endpoints-horizontal">
                                                        <div className="endpoint-horizontal">
                                                            <FiMapPin className="endpoint-icon" />
                                                            <div className="endpoint-info">
                                                                <p className="endpoint-address">{selectedTrip.origin?.placeAddress || 'N/A'}</p>
                                                                <p className="endpoint-time">{moment(selectedTrip.startTime).format('hh:mm A')}</p>
                                                            </div>
                                                        </div>
                                                        <div className="endpoint-horizontal">
                                                            <FiFlag className="endpoint-icon" />
                                                            <div className="endpoint-info">
                                                                <p className="endpoint-address">{selectedTrip.destination?.placeAddress || 'N/A'}</p>
                                                                <p className="endpoint-time">{moment(selectedTrip.endTime).format('hh:mm A')}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="timeline-column">
                                <div className="timeline-container" onClick={(e) => {
                                    // Only deselect if clicking on the container itself, not on timeline items
                                    if (e.target === e.currentTarget) {
                                        handleDeselect();
                                    }
                                }}>
                                    <div className="timeline-list">
                                        {timelineData.map(item => renderTimelineItem(item))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            
            {/* Image Modal */}
            {selectedImage && (
                <div className="image-modal" onClick={() => setSelectedImage(null)}>
                    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="image-modal-close" onClick={() => setSelectedImage(null)}>×</button>
                        <img src={selectedImage} alt="Work log image" />
                    </div>
                </div>
            )}
        </div>
    </div>
    );
};

export default LocationPage;